/**
 * ImprovementPRReview — in-Mission-Control PR review + Approve & Deploy.
 *
 * Mirrors the AgentFleet Console's Review tab (FleetDetail ReviewTab/PatchView)
 * so an operator reviewing an improvement's proposed change never has to leave
 * Mission Control for GitHub or the console: the diff, CI checks, and the
 * merge-to-develop + promote-to-production actions all live here.
 *
 * - operator | fleet_owner: can view the diff + checks — including a CORE
 *   change, which is readable but not actionable here (ADR-122 D-core-read).
 *   Before that, opening one returned a repo-mismatch error and the owner was
 *   told they had no access to a change that affects their own fleet.
 * - fleet_owner only: Approve & Deploy (merge → develop, then promote →
 *   main; deploy fires from the main merge) or Deny (comment + close).
 *
 * NOTE (follow-up): approving here does not update the improvement signal's
 * stored status (there is no internal API for that transition today — see
 * backend/internal/handler/improvements_pr.go). Denying does: it records the
 * row as `declined`. This page always reflects live GitHub PR state instead.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { api, ForbiddenError, UnauthorizedError } from '../api/client'
import type { ApproveImprovementPRResult, DeployInfo, DetailState, FailingCheck, ImprovementPR, ImprovementPRCheckRunGroup, ImprovementPRChecksRerun } from '../api/client'
import { useAuth } from '../context/AuthContext'
import { PatchView } from '../components/PatchView'
import { ChangeJourney } from '../components/ChangeJourney'
import { approveConfirmation } from '../components/ReadyWorklist'
import type { ApproveOutcome } from '../components/ChangeJourney'
import { deployLiveState } from '../utils/changeJourneySteps'
import { inflightActions } from '../utils/inflightActions'
import { FailingChecksPanel } from '../components/FailingChecksPanel'
import { gateInconclusiveReasonText } from '../components/GateInconclusiveNote'

/** Extracts {status, message, checks} from the "HTTP <status>: <json-body>"
 * errors thrown by the api client. `checks` is only present on a 409
 * `checks_failing` body (see ApproveImprovementPR, backend) — carried
 * through so an attempted approve can render the same rich panel as the
 * proactive fetch, rather than falling back to `message` alone.
 *
 * `detailState`/`detailMessage` come from the same body and must travel with
 * the rows: a 409 that returned SOME rows plus an enumeration error would
 * otherwise render under the initial `complete` state, presenting a partial
 * answer as the whole one. */
function parseApiError(
  err: unknown,
  fallback: string,
): {
  status: number | null
  code: string
  message: string
  checks?: FailingCheck[]
  detailState?: DetailState
  detailMessage?: string
} {
  if (!(err instanceof Error)) return { status: null, code: '', message: fallback }
  const m = /^HTTP (\d+): ([\s\S]*)$/.exec(err.message)
  if (!m) return { status: null, code: '', message: err.message || fallback }
  const status = Number(m[1])
  let message = fallback
  let code = ''
  let checks: FailingCheck[] | undefined
  let detailState: DetailState | undefined
  let detailMessage: string | undefined
  try {
    const body = JSON.parse(m[2]) as {
      error?: string
      detail?: string
      checks?: FailingCheck[]
      detail_state?: DetailState
      detail_message?: string
    }
    code = body.error || ''
    message = body.detail || body.error || fallback
    checks = body.checks
    detailState = body.detail_state
    detailMessage = body.detail_message
  } catch {
    /* not JSON */
  }
  return { status, code, message, checks, detailState, detailMessage }
}

/** Why Approve & Deploy is disabled, for the button's title/subtext — "" when
 * it isn't. `failingChecks` takes precedence over `checks` because it can be
 * populated from an attempted-approve's 409 body even when the PR's own
 * `checks` field is stale/degraded (see ApproveImprovementPR's 405 handling,
 * backend) — the real bug this feature fixes. */
/** NOTE on `unreadable`: it is deliberately NOT a disable reason.
 *
 *  spartan #950 was green, CLEAN, and mergeable, and an earlier version of this
 *  feature refused it because Mission Control could not READ its checks. MC's
 *  blindness is not evidence about the checks; branch protection is the authority and
 *  it can see them. A failing check still blocks — via the merge attempt's own
 *  required-checks error, which surfaces the same panel. */
function approveDisabledReason(checks: ImprovementPR['checks'], failingChecks: FailingCheck[]): string {
  const counted = countedFailures(failingChecks)
  if (checks === 'failing' || counted.length > 0) {
    // Not "required checks": nothing on this route can tell a required failing
    // job from an optional one, so the count says only what was observed.
    const count = counted.length > 0 ? `${counted.length} check${counted.length === 1 ? '' : 's'}` : 'checks'
    return `Can't approve — ${count} failing — fix the failing checks first`
  }
  if (checks === 'pending') {
    return "Checks are still running — can't approve until they finish"
  }
  // `stalled` MUST disable. The approve endpoint groups Stalled with Failing and
  // answers 409 checks_failing, so leaving the button live promised something the
  // server refuses. It was unreachable on a fleet until the checks read started
  // resolving to a real verdict, which is why the split went unnoticed.
  if (checks === 'stalled') {
    return "A check stopped before finishing — re-run it before approving"
  }
  return ''
}

/** The rows that actually establish a failure. An `unenumerable` row says only
 * that Mission Control could not read a run's jobs, so counting it would report
 * an unreadable run to the operator as a definite failure. */
function countedFailures(checks: FailingCheck[]): FailingCheck[] {
  return checks.filter(c => c.kind !== 'unenumerable')
}

/** ADR-110 Layer B — the terminal-state copy's success/failure split, derived
 * from the same deployLiveState ChangeJourney's stepper uses, so the banner
 * text and the stepper badge can never disagree about what happened. */
function isDeployLive(deploy?: DeployInfo | null): boolean {
  return deployLiveState(deploy) === 'done'
}
function isDeployFailed(deploy?: DeployInfo | null): boolean {
  return deployLiveState(deploy) === 'failed'
}

function checksBadgeClass(checks: string): string {
  switch (checks) {
    case 'passing': return 'bg-green-100 text-green-800'
    case 'failing': return 'bg-red-100 text-red-800'
    case 'pending': return 'bg-amber-100 text-amber-800'
    default: return 'bg-gray-100 text-gray-500'
  }
}

function checkRunBadgeClass(conclusion: string): string {
  switch (conclusion) {
    case 'success': return 'bg-green-100 text-green-800'
    case 'failure': return 'bg-red-100 text-red-800'
    default: return 'bg-amber-100 text-amber-800'
  }
}

/** How long an accepted bring-up-to-date keeps its own poll running before it
 *  admits nothing arrived, and how often that poll re-reads. Bounded on
 *  purpose: GitHub accepts the update asynchronously and may never produce a
 *  new head, and a poll with no end is a spinner that lies. */
const UPDATE_PENDING_POLL_MS = 5000
const UPDATE_PENDING_TIMEOUT_MS = 120000

/** How long the page keeps re-reading a verdict of `unreadable` before it
 *  accepts that the read will not clear on its own.
 *
 *  `unreadable` is mostly permanent — spartan #939 is a refused read polled
 *  forever — but it is also what a fleet PR's first seconds return when the
 *  primary read is refused and GitHub has not yet created a run for the push,
 *  and a 403 is how GitHub reports a rate limit as well, which lifts by itself.
 *  With no poll at all, whoever opened this page inside that window watched a
 *  red "could not establish a checks verdict" that never changed while the
 *  checks passed on GitHub (matthaios #463). A bounded poll clears the
 *  transient case and still stops on the permanent one. */
const UNREADABLE_POLL_WINDOW_MS = 120000

/** Why a Re-run control is not offered — "" when it is.
 *
 *  `unavailable` is declared by the credential Mission Control was started
 *  with, so it is a fact about this deployment rather than a guess from a read.
 *  The second case is about the change: a re-run replays the payload its run
 *  was created with, and on #1100 that was OBSERVED to check out a merge commit
 *  built on the fleet as it was before the fix — two recovery attempts at
 *  22:46:18Z both landed on the pre-fix base, and only moving the head passed
 *  (ADR-122 D5c). ADR-122 D5c records poll-then-trigger as unverified rather
 *  than disproven, so this says what was seen and claims no proof.
 *
 *  `isCoreChange` changes the ANSWER, not just the wording. Bring-up-to-date is
 *  rendered only for a fleet's own change and the server refuses it 403 for a
 *  core one, so telling a core-change operator to bring it up to date named a
 *  control that is not on their screen and a step the server would not accept. */
function rerunHiddenReason(
  capability: ImprovementPRChecksRerun,
  notCheckedSinceAndFailing: boolean,
  isCoreChange: boolean,
): string {
  if (capability === 'unavailable') {
    return 'Mission Control cannot start these checks again for this fleet — open them on GitHub to run them there.'
  }
  if (notCheckedSinceAndFailing) {
    return isCoreChange
      ? 'Starting these checks again would test the platform as it was, not as it is now. ' +
          'This change lives in the platform’s own repository, so bringing it up to date is ' +
          'the platform team’s to do — open it on GitHub to follow what happens to it.'
      : 'Starting these checks again would test your fleet as it was, not as it is now — bring the change up to date instead.'
  }
  return ''
}

/**
 * How many executions the current answers replaced. `superseded` is the
 * backend's count, and it is absent only on an older backend — where `history`
 * is absent too and the answer is 0.
 */
function earlierRunTotal(runs: ImprovementPRCheckRunGroup[]): number {
  return runs.reduce((total, cr) => total + (cr.superseded ?? 0), 0)
}

/** The note a failed write leaves on screen. Written once for both writes on
 *  this page: the bare "The <noun> request failed." fitted a coded refusal and a
 *  bodyless proxy 502 equally well and settled neither, so the status travels
 *  with it whenever the body carried no sentence of its own. */
function requestFailureNote(err: unknown, noun: string): string {
  const { status, message } = parseApiError(err, '')
  if (message) return message
  return status === null
    ? `The ${noun} request failed.`
    : `The ${noun} request failed (HTTP ${status}) and came back with no explanation.`
}

export function ImprovementPRReview() {
  const { id } = useParams<{ id: string }>()
  // The router's key for THIS entry in the history. Two visits to the same
  // change are two entries with two keys, even when React commits only the
  // second of them.
  const { key: locationKey } = useLocation()
  const { user } = useAuth()
  const canApprove = user.fleet_role === 'fleet_owner'

  const [pr, setPr] = useState<ImprovementPR | null>(null)
  // ADR-122 D-core-read: a CORE change is readable here but not actionable — it
  // fans out to every fleet, is reviewed by the platform, and is never merged
  // from a fleet (ADR-054, ADR-103). `owned_by_this_fleet` is absent on an older
  // backend, which then behaves exactly as before (=== false, not falsy).
  const isCoreChange = pr?.owned_by_this_fleet === false
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [unconfigured, setUnconfigured] = useState(false)
  const [notFound, setNotFound] = useState(false)

  // Locked while an action for THIS change is running, whichever visit or
  // mounted page sent it -- see inflightActions. Re-rendered when the set of
  // running actions changes.
  const [, actionsChanged] = useState(0)
  useEffect(() => inflightActions.subscribe(() => actionsChanged((n) => n + 1)), [])
  const busy = !!id && inflightActions.has(id)
  const [actionError, setActionError] = useState<string | null>(null)
  const [approveResult, setApproveResult] = useState<ApproveImprovementPRResult | null>(null)
  const [approveOutcome, setApproveOutcome] = useState<ApproveOutcome | undefined>(undefined)
  const [denying, setDenying] = useState(false)
  const [denyReason, setDenyReason] = useState('')
  const [denyDone, setDenyDone] = useState(false)
  // Set when the PR closed but its head branch could not be deleted, so the
  // operator learns the branch (a version reservation on a publish lane) is
  // still held rather than assuming the deny undid everything.
  const [denyBranchError, setDenyBranchError] = useState<{ branch: string; detail: string } | null>(null)
  const [denyStatusError, setDenyStatusError] = useState<string | null>(null)

  // The "why can't I approve this" data — fetched proactively whenever the
  // PR isn't fully green, and re-populated straight from an attempted
  // approve's 409 body if that happens anyway (see handleApprove).
  const [failingChecks, setFailingChecks] = useState<FailingCheck[]>([])
  // How complete that read was. A refused or truncated one must not render as
  // a complete account of the change (ADR-122 D3a). Defaults to `complete`,
  // which is also what an older backend's response reads as.
  const [detailState, setDetailState] = useState<DetailState>('complete')
  const [detailMessage, setDetailMessage] = useState<string | undefined>(undefined)
  const [rerunNote, setRerunNote] = useState('')

  // BRING UP TO DATE IS ACCEPTED, NOT DONE.
  //
  // GitHub answers the update with a 202 and no commit, so "the update was
  // requested" and "the new checks are reporting" are two separate claims and
  // the page may only make the first one until it has seen a new commit.
  // `updatePending` is what keeps it looking, and it is its OWN state rather
  // than a reuse of `checksUnsettled`: that one runs only for pending/none
  // checks or a pending deploy, and the change this action exists for has
  // FAILED checks, so it would never have been re-read at all.
  const [updateNote, setUpdateNote] = useState('')
  // ONE state for one fact: the commit the update was AIMED at, which the
  // response names. Non-null IS "pending" — a separate boolean beside it had to
  // be set and cleared in lockstep at six sites, and any site that forgot one
  // of the two left the page polling for a commit it no longer knew, or knowing
  // one it was no longer looking for.
  //
  // THE REQUEST HEAD, NOT THE FIRST OBSERVED ONE. Baselining off the forced
  // re-read that follows the request discarded the distinction the response was
  // carrying: an update that completed BEFORE that re-read made its own result
  // the baseline, so the head never moved again, and the page went on to report
  // that nothing had reported while the update had landed and the checks had
  // passed. Comparing against the request head recognises that case on the
  // first observation.
  const [updateWatch, setUpdateWatch] = useState<{ requestSHA: string } | null>(null)
  const updatePending = updateWatch !== null
  // The same fact as a ref, because the READ records its own observation and a
  // read is not a render. Written only through armUpdateWatch, so the two
  // cannot drift.
  const updateWatchRef = useRef<{ requestSHA: string } | null>(null)
  // WHAT HAS ACTUALLY BEEN SEEN while the update is unresolved, so the timeout
  // states only what is still unconfirmed. Head movement and checks reporting
  // are tracked apart because they are different facts and neither one is proof
  // that the fleet's changes were incorporated. A ref, not state: the poll's
  // interval is keyed on `updatePending` and an observation must not restart it.
  const updateObserved = useRef({ headMoved: false, checksReported: false })
  const armUpdateWatch = useCallback((requestSHA: string | null) => {
    updateWatchRef.current = requestSHA === null ? null : { requestSHA }
    setUpdateWatch(updateWatchRef.current)
  }, [])
  // OBSERVED AT THE READ, not at the next render. Recording head movement from
  // an effect made it depend on when React chose to flush one, and the bounded
  // wait below reads these the moment it expires — so a page that had seen the
  // change move could still time out saying it had not. The read has the fresh
  // change in hand, so this is where the observation belongs.
  const noteUpdateObservation = useCallback((p: ImprovementPR) => {
    const watch = updateWatchRef.current
    if (!watch || p.head_sha === watch.requestSHA) return
    updateObserved.current.headMoved = true
    if ((p.check_runs?.length ?? 0) > 0) updateObserved.current.checksReported = true
  }, [])

  // WHICH ROUTE AND WHICH COMMIT THIS PAGE IS CURRENTLY SHOWING.
  //
  // Both reads below outlive the state they were started for: the operator can
  // navigate to another improvement, and a poll can move the PR to a new head
  // commit, while a request is still open. Every response is checked against
  // these refs before it writes anything. Refs, not state, so an in-flight
  // closure sees the current values rather than the ones it captured.
  const activeId = useRef(id)
  // An action's reply can arrive after the page is gone. The refs above are
  // frozen at unmount and still match, so without this the dead page's handler
  // would go on to issue reads and writes of its own.
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const headSHARef = useRef<string | undefined>(undefined)

  // WHICH VISIT TO THIS ROUTE IS ON SCREEN.
  //
  // The route id and the head SHA together do not identify a visit. Leave a
  // change and come back to it at the same commit and both match, so an approve
  // or a deny still open from the earlier visit passed every guard and wrote its
  // verdict onto the new one — the page claimed a deploy this operator never
  // asked for. This counter separates the two visits, and it also tells a
  // REPLAYED mount from a navigation: StrictMode deliberately re-runs effects,
  // and treating that replay as a route change retired the open PR read and
  // started a second one on top of it, so the two could resolve out of order and
  // an older `passing` answer could enable Approve on a red change.
  const routeEpoch = useRef(0)
  const activeLocationKey = useRef<string | undefined>(undefined)

  // Which PR read is the current one. Single-flight alone is not enough once an
  // action can force a fresh read past an open one: both are then in flight and
  // the older must not write.
  const loadGeneration = useRef(0)

  // Detail reads are not ordered: a slow one started while the checks were
  // pending can land after a later PR read has already found them green, and
  // whichever answer arrives last wins. That stranded a passing, approvable PR
  // on "could not read which checks are failing" with no way out but a reload,
  // and in the other direction let obsolete failing rows re-disable Approve.
  //
  // The ordering is fixed by SINGLE-FLIGHT, not by superseding: one request per
  // `${id}:${headSHA}` at a time, and a caller asking for a key already in
  // flight joins that request instead of starting a competing one. A generation
  // remains, but only for genuine invalidation — the route identity changed, or
  // an authoritative answer (a settled PR read, an approve's 409) replaced the
  // detail outright.
  //
  // Superseding on EVERY request is what this replaces, and it starved the
  // panel. The poll fires every 5 seconds while checks are pending, so a detail
  // read taking longer than that — which the backend explicitly allows, up to
  // 20 seconds for log work — was retired before it could land, and the panel
  // stayed empty for as long as the checks ran.
  const detailGeneration = useRef(0)
  const detailFlight = useRef<{ key: string; promise: Promise<void> } | null>(null)

  const loadFailingChecks = useCallback((headSHA: string | undefined): Promise<void> => {
    if (!id) return Promise.resolve()
    const key = `${id}:${headSHA ?? ''}`
    if (detailFlight.current?.key === key) return detailFlight.current.promise
    const generation = ++detailGeneration.current
    const promise = (async () => {
      try {
        const res = await api.getImprovementPRFailingChecks(id)
        if (generation !== detailGeneration.current) return
        setFailingChecks(res.checks)
        setDetailState(res.detail_state ?? 'complete')
        setDetailMessage(res.detail_message)
      } catch {
        if (generation !== detailGeneration.current) return
        // A failed read is itself something the operator has to know. Resetting
        // to `complete` hid the panel entirely, so a refused or broken detail
        // read looked like "there is nothing to say about these checks".
        setFailingChecks([])
        setDetailState('unreadable')
        setDetailMessage('Mission Control could not read this change\u2019s check detail — see the checks on GitHub.')
      }
    })()
    detailFlight.current = { key, promise }
    void promise.finally(() => {
      if (detailFlight.current?.promise === promise) detailFlight.current = null
    })
    return promise
  }, [id])

  /** Retires whatever detail read is open, so a newer and authoritative answer
   * cannot be overwritten by it. */
  const invalidateDetail = useCallback(() => {
    detailGeneration.current++
    detailFlight.current = null
  }, [])

  // The PR read is single-flight for the same reason, and this one is not
  // cosmetic: two overlapping `load()`s could resolve out of order, and an older
  // `passing` response landing after a newer `failing` one cleared the panel,
  // ENABLED Approve on a red change, and stopped the poll that would have
  // corrected it.
  const loadFlight = useRef<{ key: string; promise: Promise<void> } | null>(null)

  // `force` is for a read taken AFTER an action completed: joining a read that
  // started before the action answers for the commit as it was, which is the
  // wrong question.
  const load = useCallback((force = false): Promise<void> => {
    if (!id) return Promise.resolve()
    if (!force && loadFlight.current?.key === id) return loadFlight.current.promise
    const visit = routeEpoch.current
    const generation = ++loadGeneration.current
    const isCurrent = () =>
      mounted.current && activeId.current === id && routeEpoch.current === visit && loadGeneration.current === generation
    setLoading(true)
    setLoadError(null)
    setUnconfigured(false)
    setNotFound(false)
    const promise = (async () => {
      try {
        const p = await api.getImprovementPR(id)
        if (!isCurrent()) return
        headSHARef.current = p.head_sha
        noteUpdateObservation(p)
        setPr(p)
        // `stalled` loads detail too. It is orion #660's shape — an Actions run
        // that ended `cancelled` — and ADR-130 D2 split it out from `failing` so
        // the two would not be confused, not so that one of them would go
        // unexplained. Leaving it out told the operator to re-run a check the
        // page then refused to name.
        if (p.checks === 'failing' || p.checks === 'pending' || p.checks === 'stalled') {
          void loadFailingChecks(p.head_sha)
        } else {
          // This PR read establishes there is no detail to show, so it retires
          // any request still open from an earlier, unsettled read.
          invalidateDetail()
          setFailingChecks([])
          setDetailState('complete')
          setDetailMessage(undefined)
        }
      } catch (err) {
        if (err instanceof UnauthorizedError) throw err
        if (!isCurrent()) return
        if (err instanceof ForbiddenError) {
          setLoadError('You do not have permission to view this PR.')
        } else {
          const { status, message } = parseApiError(err, 'Failed to load the PR.')
          if (status === 503) setUnconfigured(true)
          else if (status === 404) setNotFound(true)
          else setLoadError(message)
        }
      } finally {
        if (isCurrent()) setLoading(false)
      }
    })()
    loadFlight.current = { key: id, promise }
    void promise.catch(() => {}).finally(() => {
      if (loadFlight.current?.promise === promise) loadFlight.current = null
    })
    return promise
  }, [id, loadFailingChecks, invalidateDetail, noteUpdateObservation])

  // Route identity changed: everything in flight belongs to the previous
  // improvement and must not write onto this one, and NOTHING the previous one
  // put on screen may still be there. Declared BEFORE the effect that starts
  // the load, so it has already run when that load resolves.
  //
  // Every control on this page closes over the current route id, so leaving the
  // previous change rendered is not a cosmetic lag: the operator reads one
  // change's title and diff while Approve merges the other one. `denyDone` was
  // worse still — denying one change hid the actions on every change opened
  // after it, for the life of the page.
  //
  // A LAYOUT effect, because a passive one permits a paint with the previous
  // change's buttons live and clickable.
  useLayoutEffect(() => {
    // A replayed mount, or any re-run of this effect for the entry already on
    // screen, is not a route change and must not retire what is in flight.
    // The id alone cannot tell: one event can navigate away and straight back,
    // and React then commits only the final location -- the same id, a new
    // history entry. That IS a new visit, and the location key says so.
    if (routeEpoch.current > 0 && activeId.current === id && activeLocationKey.current === locationKey) return

    activeId.current = id
    activeLocationKey.current = locationKey
    routeEpoch.current++
    loadGeneration.current++
    headSHARef.current = undefined
    invalidateDetail()
    loadFlight.current = null

    setPr(null)
    setLoading(true)
    setLoadError(null)
    setUnconfigured(false)
    setNotFound(false)

    setFailingChecks([])
    setDetailState('complete')
    setDetailMessage(undefined)
    setRerunNote('')
    setUpdateNote('')
    armUpdateWatch(null)
    updateObserved.current = { headMoved: false, checksReported: false }

    setActionError(null)
    setApproveResult(null)
    setApproveOutcome(undefined)
    setDenying(false)
    setDenyReason('')
    setDenyDone(false)
    setDenyBranchError(null)
    setDenyStatusError(null)
  }, [id, locationKey, invalidateDetail, armUpdateWatch])

  // One read per VISIT, not per id: the reset above runs for a new history
  // entry that carries the id already on screen, and a page reset with no read
  // behind it would say "Loading PR" for good.
  useEffect(() => {
    void load()
  }, [load, locationKey])

  // A visit that inherits a running action -- an approve sent from an earlier
  // visit to this change, or from a page since unmounted -- shows neither that
  // action's verdict nor the change as it was before it. When the action ends
  // the change is re-read, so the screen shows what it has become.
  useEffect(() => {
    if (!id) return
    const running = inflightActions.running(id)
    if (!running) return
    let live = true
    void running.then(() => {
      if (live) void load(true)
    })
    return () => {
      live = false
    }
  }, [id, locationKey, load])

  // ADR-110 Layer B: a merged PR's "Live in production" verdict depends on
  // the actual Deploy Fleet run, which may still be queued/in_progress (or
  // not started at all yet) at the moment this page first loads it — poll
  // until that run reaches a terminal, change-covering result so the
  // operator sees the real conclusion without a manual refresh. Modeled on
  // useFleetVersion's setInterval pattern.
  const deployPending = !!pr?.merged && (!pr.deploy || pr.deploy.status === 'none' || !pr.deploy.includes_change || pr.deploy.status !== 'completed')

  // The same argument BEFORE the merge, which is where it was missing: an open
  // PR's checks are still running when the page first loads, and nothing re-read
  // them. So the verdict an approver saw was frozen at load time.
  //
  // orion #555 is what that costs. A delete PR opened at 18:13:57 and `validate`
  // failed 30s later; anyone who opened this page inside that window saw
  // "checks: pending" — a disabled Approve and the message "the proposal's
  // automated checks are still running", which reads as "wait", not "broken" —
  // and it never changed. No poll, no notification, no second signal anywhere.
  // The failure was only ever visible to someone who happened to reload.
  //
  // `none` is included deliberately: it is the state before any check run has
  // REPORTED, which is exactly the first second of a PR's life. Treating it as
  // settled is what let a not-yet-failed PR look approvable.
  const checksUnsettled = !!pr && !pr.merged && (pr.checks === 'pending' || pr.checks === 'none')
  // The Live step waits for the deploy, which is read only once the PR reads as
  // merged. The re-read straight after an approve can still say it isn't, and with
  // passing checks nothing else would ask again.
  const approvedNotYetMerged = !!pr && !pr.merged && !!approveOutcome?.merged_to_develop && !approveOutcome.promoteFailed
  useEffect(() => {
    if (!deployPending && !checksUnsettled && !approvedNotYetMerged) return
    const t = setInterval(() => { void load() }, 5000)
    return () => clearInterval(t)
  }, [deployPending, checksUnsettled, approvedNotYetMerged, load])

  // The bounded unreadable poll. Separate from the one above because it has an
  // end: `unreadable` is not a state waiting is known to clear, so this asks
  // for UNREADABLE_POLL_WINDOW_MS and then stops, leaving the banner in place.
  // The clock starts when the verdict becomes unreadable and is not restarted
  // by re-renders, because nothing in the dependency list changes within one
  // unreadable spell. It DOES restart if the verdict leaves unreadable and comes
  // back, and a route change resets `pr`, which ends the spell.
  const checksUnreadable = !!pr && !pr.merged && pr.checks === 'unreadable'
  useEffect(() => {
    if (!checksUnreadable) return
    const startedAt = Date.now()
    const t = setInterval(() => {
      if (Date.now() - startedAt >= UNREADABLE_POLL_WINDOW_MS) {
        clearInterval(t)
        return
      }
      void load()
    }, UPDATE_PENDING_POLL_MS)
    return () => clearInterval(t)
  }, [checksUnreadable, load])

  // The update-pending poll. Separate from the one above BECAUSE of what it is
  // for: the change an operator brings up to date is typically one whose checks
  // have already failed, and `checksUnsettled` is false in exactly that state.
  // Reusing it would have left the page saying "this page will update as the
  // new checks report" over data that never changed again.
  useEffect(() => {
    if (!updatePending) return
    const startedAt = Date.now()
    const t = setInterval(() => {
      if (Date.now() - startedAt >= UPDATE_PENDING_TIMEOUT_MS) {
        armUpdateWatch(null)
        // ONLY WHAT IS STILL UNCONFIRMED. The one sentence this used to print
        // said no new checks had reported, which was a positive claim about
        // checks — and it was printed over a change whose update had landed and
        // whose checks had passed. Each branch below states what was observed
        // and names the part Mission Control could not confirm.
        const { headMoved, checksReported } = updateObserved.current
        setUpdateNote(
          !headMoved
            ? 'The update was requested, but this change is still on the commit it was on. ' +
                'Open this change on GitHub to see what happened to it.'
            : checksReported
              ? 'This change is on a new commit and its checks have reported, but Mission Control ' +
                  'could not confirm that the change now includes your fleet’s latest changes. ' +
                  'Open this change on GitHub to see where it stands.'
              : 'This change is on a new commit, but Mission Control could not confirm that the ' +
                  'change now includes your fleet’s latest changes. ' +
                  'Open this change on GitHub to see where it stands.',
        )
        return
      }
      void load()
    }, UPDATE_PENDING_POLL_MS)
    return () => clearInterval(t)
  }, [updatePending, load, armUpdateWatch])

  // WHAT THE PAGE MAY SAY WHILE AN ACCEPTED UPDATE IS UNRESOLVED.
  //
  // Two different facts, and the page had been treating one as the other. A new
  // commit is head MOVEMENT, and nothing more: an agent pushing an unrelated
  // fix while the update was still unresolved moved the head too, and on that
  // alone the page announced the change as brought up to date and stopped
  // looking — with `base_state` still saying nothing had been checked since the
  // fleet moved.
  //
  // So head movement is described as head movement, and the up-to-date claim
  // waits for the evidence that something has actually been checked since the
  // fleet's own branch moved: `base_state` reaching `checked_since`. That is
  // the only signal on this page that speaks about the base at all.
  useEffect(() => {
    if (!pr || !updateWatch) return
    if (pr.base_state?.state === 'checked_since') {
      armUpdateWatch(null)
      setUpdateNote('This change was brought up to date with your fleet. This page will update as its checks report.')
      return
    }
    if (updateObserved.current.headMoved) {
      setUpdateNote(
        'This change is on a new commit. Mission Control cannot confirm yet that the change ' +
          'includes your fleet’s latest changes — this page will update as its checks report.',
      )
    }
  }, [pr, updateWatch, armUpdateWatch])

  // The recovery action. `rerun: 0` is REPORTED, not swallowed: a PR whose checks
  // were never created has nothing to re-run, and saying so is what tells the
  // operator the next move is reopening rather than waiting on a spinner.
  const handleRerun = useCallback(async () => {
    if (!id) return
    // The note is a claim about the checks at ONE commit — the one that was
    // re-run, which the response names. Shown beside a newer commit's checks it
    // tells the operator to wait for runs that will never report against what
    // they are looking at. So the note lands only if the page is still on that
    // commit after the re-read, and the response's own `head_sha` is what it is
    // compared against rather than the SHA captured before the request.
    const requestId = id
    const visit = routeEpoch.current
    const actionIsCurrent = () =>
      mounted.current && activeId.current === requestId && routeEpoch.current === visit
    const endAction = inflightActions.begin(requestId)
    setRerunNote('')
    try {
      let res: Awaited<ReturnType<typeof api.rerunImprovementPRChecks>>
      // Only the request itself decides whether the re-run failed. The reads
      // that follow report on it and are not part of its outcome.
      try {
        res = await api.rerunImprovementPRChecks(requestId)
      } catch (e) {
        if (!actionIsCurrent()) return
        setRerunNote(requestFailureNote(e, 're-run'))
        return
      }
      if (!actionIsCurrent()) return
      // Forced, because a read already open predates the re-run and would answer
      // for the commit as it was before it. Its failure is swallowed here: `load`
      // rethrows an expired session past its own guard, and letting that reach
      // the handler's error path told the operator a completed re-run had
      // failed. Whatever went wrong with the read, `load` has already put on
      // screen.
      await load(true).catch(() => {})
      if (!actionIsCurrent() || headSHARef.current !== res.head_sha) return
      // A PARTIAL re-run is not a clean one, and the server says so. When
      // `refused > 0` the response carries the sentence that names both halves,
      // and it wins: rendering `rerun` alone told the operator every failed
      // check had been started again and left them waiting on one that never
      // was. Same defect as a write refusal shown as an untroubled outcome.
      setRerunNote(
        res.detail ||
          (res.rerun > 0
            ? `Re-ran ${res.rerun} check run${res.rerun === 1 ? '' : 's'} — this page will update as they report.`
            : 'Nothing was re-runnable: no failed check runs exist at this commit. The checks may never have been created.'),
      )
      await loadFailingChecks(res.head_sha)
    } finally {
      endAction()
    }
  }, [id, load, loadFailingChecks])

  /** Bring the change up to date with the fleet, so the next check run tests it
   *  against the fleet as it is now. Not a re-run: a re-run replays the payload
   *  its run was created with, which is what stranded #1100. */
  const handleUpdateBranch = useCallback(async () => {
    if (!id) return
    const requestId = id
    const visit = routeEpoch.current
    const actionIsCurrent = () =>
      mounted.current && activeId.current === requestId && routeEpoch.current === visit
    const endAction = inflightActions.begin(requestId)
    setUpdateNote('')
    try {
      // A 200 is the only success shape: every refusal is an error status with
      // a coded body, so there is no not-accepted branch to render.
      const res = await api.updateImprovementPRBranch(requestId)
      if (!actionIsCurrent()) return
      // ACCEPTED, and nothing more. No claim about checks existing, running or
      // passing — GitHub has not created them yet and may not.
      //
      // Forced re-read before arming the poll, so a change that has already
      // moved is on screen — and the baseline is the head the REQUEST was aimed
      // at, which the response names, so that re-read is itself an observation
      // rather than the baseline. An update that completed before it is then
      // recognised instead of being read as the starting point.
      //
      // A new commit alone no longer announces the update as landed, so the
      // ordinary push-in-between this used to mistake for a landed update is
      // reported as what it is: the change is on a new commit, unconfirmed.
      updateObserved.current = { headMoved: false, checksReported: false }
      // Armed BEFORE the re-read, so that re-read is an observation the watch
      // sees. Armed after it, an update that had already completed was invisible.
      // The requested note is set BEFORE the re-read, not after it. Set
      // afterwards it raced the observation the re-read produces and could
      // overwrite what the page had just learned about the change.
      setUpdateNote('The update was requested — this page will update as the new checks report.')
      armUpdateWatch(res.head_sha_at_request || headSHARef.current || '')
      await load(true).catch(() => {})
    } catch (e) {
      if (!actionIsCurrent()) return
      armUpdateWatch(null)
      setUpdateNote(requestFailureNote(e, 'update'))
    } finally {
      endAction()
    }
  }, [id, load, armUpdateWatch])

  const handleApprove = useCallback(async () => {
    if (!id) return
    if (!window.confirm(approveConfirmation())) {
      return
    }
    // An approve can take longer than it takes an operator to move on, and its
    // answer describes THIS change at THIS commit. Captured before the request
    // and re-checked on every resolution path: a reply that arrived after the
    // operator navigated to another improvement used to install its check rows
    // into that improvement's panel, so one change's failures were read as
    // another's.
    const requestId = id
    const requestSHA = headSHARef.current
    const visit = routeEpoch.current
    const stillCurrent = () =>
      mounted.current &&
      activeId.current === requestId &&
      routeEpoch.current === visit &&
      headSHARef.current === requestSHA
    const endAction = inflightActions.begin(requestId)
    setActionError(null)
    try {
      const res = await api.approveImprovementPR(id)
      if (!stillCurrent()) return
      setApproveResult(res)
      setApproveOutcome({ merged_to_develop: res.merged_to_develop, promoted: res.promoted, queued: res.queued })
      void load()
    } catch (err) {
      if (err instanceof UnauthorizedError) throw err
      if (!stillCurrent()) return
      if (err instanceof ForbiddenError) {
        setActionError('Permission denied — fleet owner required.')
      } else {
        const { code, message, checks, detailState: errDetailState, detailMessage: errDetailMessage } = parseApiError(err, 'Approve failed.')
        if (code === 'promote_failed') {
          // Merged to develop but the develop→main promote failed — the change
          // is safely on develop. This is the ONLY failure where a merge happened.
          // Take live is what retries a promote; the deploy workflow only deploys
          // main. A racing approve can also have promoted this change, so the
          // message and the link say to look, not that it is waiting.
          setActionError(message)
          setApproveOutcome({ merged_to_develop: true, promoted: false, queued: false, promoteFailed: true })
        } else if (code === 'checks_failing') {
          // The exact bug this feature fixes: GitHub (or MC's own gate)
          // blocked the merge on failing required checks. Render the same
          // rich FailingChecksPanel the proactive fetch would show — never
          // the raw upstream error — using the checks the backend saw at
          // merge time (may differ from the proactive fetch if a check just
          // finished).
          // The 409 is the authoritative answer at the moment Approve was
          // pressed, so it retires any proactive read still open.
          invalidateDetail()
          setFailingChecks(checks ?? [])
          setDetailState(errDetailState ?? 'complete')
          setDetailMessage(errDetailMessage)
          if (!checks || checks.length === 0) {
            // A 409 with NO check list leaves nothing on screen: the panel
            // renders from `failingChecks` and an empty array renders nothing,
            // so the click appeared to do exactly nothing. That is the normal
            // case, not an edge one — per-check detail needs `checks: read`,
            // which no broker purpose grants, so FailingChecks returns an empty
            // list on every fleet. Fall back to the refusal the backend sent.
            setActionError(message)
          }
        } else if (code === 'checks_pending') {
          setActionError('The automated checks are still running — try again once they finish.')
        } else {
          // Any other failure (not-open, upstream error) happened BEFORE any
          // merge — do not imply the change published.
          setActionError(message)
        }
      }
    } finally {
      endAction()
    }
  }, [id, load, invalidateDetail])

  const handleDeny = useCallback(async () => {
    if (!id || !denyReason.trim()) return
    // Closing one change must not report itself as another change's outcome, so
    // this answer is bound to the improvement it was sent for, exactly as
    // approve's is.
    const requestId = id
    const visit = routeEpoch.current
    const actionIsCurrent = () =>
      mounted.current && activeId.current === requestId && routeEpoch.current === visit
    const endAction = inflightActions.begin(requestId)
    setActionError(null)
    try {
      const result = await api.denyImprovementPR(requestId, denyReason.trim())
      if (!actionIsCurrent()) return
      setDenying(false)
      setDenyDone(true)
      setDenyBranchError(
        result?.branch_delete_error ? { branch: result.branch, detail: result.branch_delete_error } : null,
      )
      setDenyStatusError(result?.status_error ?? null)
      void load()
    } catch (err) {
      if (err instanceof UnauthorizedError) throw err
      if (!actionIsCurrent()) return
      if (err instanceof ForbiddenError) {
        setActionError('Permission denied — fleet owner required.')
      } else {
        const { message } = parseApiError(err, 'Deny failed.')
        setActionError(message)
      }
    } finally {
      endAction()
    }
  }, [id, denyReason, load])

  const earlierRunCount = pr ? earlierRunTotal(pr.check_runs) : 0

  // An absent capability is `unknown`, never `unavailable`: an older backend
  // says nothing about the grant, and hiding a working control on silence
  // removes the only way out of a red change.
  const rerunCapability: ImprovementPRChecksRerun = pr?.checks_rerun ?? 'unknown'
  const baseState = pr?.base_state?.state
  // ONE definition of "these checks look failing", read by the recovery row
  // below and by the withheld-reason above it. It was written twice with two
  // meanings — only the render site carried `!pr.merged` — so on a merged change
  // that nothing had checked since the fleet moved, the reason suppressed the
  // re-run control inside the unreadable block while the recovery row it was
  // written for did not render at all. `!pr.merged` is the correct half: a
  // merged change has nothing left to recover.
  const checksLookFailing =
    !!pr && !pr.merged && (pr.checks === 'failing' || pr.checks === 'stalled' || failingChecks.length > 0)
  const rerunHidden = rerunHiddenReason(
    rerunCapability,
    baseState === 'not_checked_since' && checksLookFailing,
    isCoreChange,
  )
  const offerRerun = rerunHidden === ''
  const authorTestsReasonText = gateInconclusiveReasonText(pr?.author_tests?.reason)

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Link to="/improvements" className="text-sm text-blue-600 hover:underline">← Back to Improvements</Link>
      </div>

      {loading && <p className="text-sm text-gray-400">Loading PR…</p>}

      {unconfigured && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
          <p className="text-sm font-medium text-amber-800 mb-1">PR review isn't configured yet</p>
          <p className="text-xs text-amber-600">GitHub integration is pending for this fleet.</p>
        </div>
      )}

      {notFound && (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-400">
          No PR is linked to this improvement yet.
        </div>
      )}

      {loadError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{loadError}</div>
      )}

      {pr && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-lg font-semibold text-gray-900 truncate">{pr.title}</p>
                <p className="text-xs text-gray-500 mt-0.5 font-mono">
                  #{pr.number} · {pr.head_ref} → {pr.base_ref}
                </p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded font-medium shrink-0 ${pr.state === 'open' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`}>
                {pr.state}
              </span>
            </div>

            <ChangeJourney checks={pr.checks} approve={approveOutcome} merged={pr.merged} deploy={pr.deploy} />

            {pr.author_tests?.verdict === 'inconclusive' && (
              <div data-testid="improvement-author-tests-not-run" className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                {authorTestsReasonText && <p className="mb-1">{authorTestsReasonText}</p>}
                <p>The fleet&apos;s own tests were not run against this change in Mission Control.</p>
                <p>Its pull request checks are the first test run it has had.</p>
              </div>
            )}

            {pr.check_runs.length > 0 && (
              <div className="space-y-1.5 pt-1">
                {/* One badge per CHECK, not per execution. A check that has run
                    several times at this commit carries its older runs under
                    `history`; rendering those inline showed nine badges for
                    three checks and made two replaced failures read as
                    "still failing". */}
                <div className="flex flex-wrap gap-2" data-testid="check-run-badges">
                  {pr.check_runs.map((cr, crIndex) => (
                    <a
                      key={cr.url || `check-run-${crIndex}`}
                      href={cr.url || undefined}
                      target="_blank"
                      rel="noreferrer"
                      className={`text-[10px] px-1.5 py-0.5 rounded ${checkRunBadgeClass(cr.conclusion)}`}
                    >
                      {cr.name}: {cr.conclusion || cr.status}
                    </a>
                  ))}
                </div>
                {earlierRunCount > 0 && (
                  <details className="text-[10px] text-gray-500">
                    <summary className="cursor-pointer select-none">
                      {earlierRunCount === 1
                        ? 'Show 1 earlier run of these checks'
                        : `Show ${earlierRunCount} earlier runs of these checks`}
                    </summary>
                    <div className="flex flex-wrap gap-2 pt-1.5" data-testid="check-run-history">
                      {pr.check_runs.flatMap((cr, crIndex) =>
                        (cr.history ?? []).map((old, oldIndex) => (
                          <a
                            key={old.url || `check-run-${crIndex}-history-${oldIndex}`}
                            href={old.url || undefined}
                            target="_blank"
                            rel="noreferrer"
                            className={`text-[10px] px-1.5 py-0.5 rounded opacity-70 ${checkRunBadgeClass(old.conclusion)}`}
                          >
                            {old.name}: {old.conclusion || old.status}
                          </a>
                        )),
                      )}
                    </div>
                  </details>
                )}
              </div>
            )}

            {(!pr.merged || pr.checks === 'passing') && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${checksBadgeClass(pr.checks)}`}>
                checks: {pr.checks}
              </span>
            )}
          </div>

          {/* HAS ANYTHING CHECKED THIS SINCE THE FLEET ITSELF CHANGED.
              A claim about TIME and nothing else. It never asserts the
              converse: Mission Control cannot know which version of the fleet a
              finished check tested, because nothing on a check run carries it.

              So it does not gate the stepper, does not touch the checks verdict
              and does not turn a green change red — a passing change with this
              banner is still passing, and the banner only says nothing has been
              checked since the fleet moved.

              Operator language, deliberately: no branch, no merge ref, no
              commit count, no sha.

              A CORE change's base is the platform's own repository, not this
              fleet, so "your fleet" would be a false statement about which
              thing moved. The noun follows the change's owner.

              OPEN ONLY. A closed, unmerged change is answered `unknown` with
              "this change is no longer open, so there is nothing to check
              against the fleet" — the backend's way of saying the question does
              not apply. Gating on `!merged` alone put an amber warning on every
              denied change. */}
          {pr.state === 'open' && !pr.merged && baseState && baseState !== 'checked_since' && (
            <div
              data-testid="base-state-banner"
              className="rounded border border-amber-300 bg-amber-50 p-3 space-y-2"
            >
              <p className="text-xs text-amber-900">
                {baseState === 'not_checked_since' ? (
                  isCoreChange ? (
                    <>
                      <strong>
                        The platform has changed since anything last checked this change.
                      </strong>{' '}
                      Nothing here has been checked against the platform as it is now, so a result
                      on this page may be about the platform as it was.
                    </>
                  ) : (
                    <>
                      <strong>Your fleet has changed since anything last checked this change.</strong>{' '}
                      Nothing here has been checked against your fleet as it is now, so a result on
                      this page may be about the fleet as it was.
                    </>
                  )
                ) : (
                  <>
                    <strong>
                      Mission Control could not tell whether {isCoreChange ? 'the platform' : 'your fleet'} has
                      changed since this change was last checked.
                    </strong>{' '}
                    {pr.base_state?.reason ||
                      'It cannot say either way, so treat what is on this page as unconfirmed.'}
                  </>
                )}
              </p>
              {baseState === 'not_checked_since' && !isCoreChange && (
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => void handleUpdateBranch()}
                    disabled={busy}
                    className="px-2 py-1 text-xs rounded bg-white border border-amber-400 hover:bg-amber-100 disabled:opacity-50"
                  >
                    Bring up to date and re-check
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Outside the banner on purpose. The banner clears the moment the
              change is brought up to date, and the note that says so has to
              outlive it — otherwise the one action on this screen answers by
              making itself and its own outcome disappear. */}
          {updateNote && (
            <p data-testid="update-note" className="text-xs text-slate-700">{updateNote}</p>
          )}

          {/* `none` means no check run has REPORTED yet — not "this change has no
              checks". It is the first seconds of a PR's life, and Approve is
              enabled there, which is how a PR that was about to go red read as
              approvable (orion #555). Not a hard block: a fleet with no CI
              configured would sit at `none` forever and blocking would strand
              every change. A warning plus the poll above, which flips this to
              pending/failing within seconds, is the honest version. */}
          {!pr.merged && pr.checks === 'none' && (
            <p className="text-xs text-amber-700">
              No checks have reported yet — this change is not verified. Waiting for them to
              start; approving now may be refused.
            </p>
          )}

          {/* UNREADABLE IS NOT "WAITING", AND IT NEEDS A WAY OUT.
              spartan #939: a 403 on the checks read was reported as `none`, so this
              page said "waiting for them to start" while `validate` had already
              failed — and the 5s poll re-asked a question whose answer could never
              change. An operator watched a permanent "waiting" over a red change and
              had no action available anywhere on the screen.

              IT DOES NOT NAME A CAUSE, and it used to. The text asserted the token was
              missing `Checks: read` / `Commit statuses: read`, which this screen cannot
              know: GetImprovementPR maps EVERY RefChecksStatus error to `unreadable`,
              and a 403 is also how GitHub reports rate limiting. Worse, the remedy it
              implied — widen the token — was the wrong one for the cause that actually
              produced this on every fleet for weeks: the checks read was issued on the
              broker's `self-improve` purpose, which holds none of the three permissions
              involved, while a read-only purpose that could answer went unasked.

              IT ALSO CLAIMS NO ABSOLUTES. A first rewrite said "every way it has of
              asking GitHub failed", which is false when the Actions read succeeds and
              simply returns no runs; and "approving still works", which is false on a
              core change, where the approve control is deliberately absent. Trading an
              invented diagnosis for a different overstatement is not a fix. */}
          {!pr.merged && pr.checks === 'unreadable' && (
            <div className="rounded border border-red-300 bg-red-50 p-3 space-y-2">
              <p className="text-xs text-red-800">
                <strong>Mission Control could not establish a checks verdict.</strong> This is not
                &ldquo;no checks yet&rdquo; — the checks may already have failed.
                {!isCoreChange && (
                  <>
                    {' '}
                    <strong>You can still try Approve:</strong> GitHub&apos;s branch protection can
                    see them and makes the final decision.
                  </>
                )}{' '}
                Open it on GitHub for the check detail.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                {offerRerun ? (
                  <button
                    type="button"
                    onClick={() => void handleRerun()}
                    disabled={busy}
                    className="px-2 py-1 text-xs rounded bg-white border border-red-300 hover:bg-red-100 disabled:opacity-50"
                  >
                    Re-run checks
                  </button>
                ) : (
                  <span className="text-xs text-red-800">{rerunHidden}</span>
                )}
                <a
                  href={pr.html_url}
                  target="_blank"
                  rel="noreferrer"
                  className="px-2 py-1 text-xs rounded bg-white border border-slate-300 hover:bg-slate-100"
                >
                  See the checks on GitHub
                </a>
                {rerunNote && <span className="text-xs text-slate-700">{rerunNote}</span>}
              </div>
            </div>
          )}

          {/* A FAILING check needs the same way out. The panel below says WHAT broke;
              this says what an operator can do about it without leaving the product,
              which until now was nothing. */}
          {checksLookFailing && (
            <div className="flex flex-wrap items-center gap-2">
              {offerRerun ? (
                <button
                  type="button"
                  onClick={() => void handleRerun()}
                  disabled={busy}
                  className="px-2 py-1 text-xs rounded bg-white border border-slate-300 hover:bg-slate-100 disabled:opacity-50"
                >
                  Re-run failed checks
                </button>
              ) : (
                <>
                  {/* Offering nothing at all was the previous behaviour and it
                      left the operator on a red change with no next move. The
                      link is the move that still works. */}
                  <span className="text-xs text-slate-700">{rerunHidden}</span>
                  <a
                    href={pr.html_url}
                    target="_blank"
                    rel="noreferrer"
                    className="px-2 py-1 text-xs rounded bg-white border border-slate-300 hover:bg-slate-100"
                  >
                    See the checks on GitHub
                  </a>
                </>
              )}
              {rerunNote && <span className="text-xs text-slate-700">{rerunNote}</span>}
            </div>
          )}

          {/* The rich "why can't I approve this" panel — rendered whenever we
              have failure detail, whether from the proactive fetch (checks
              currently failing/pending) or from an attempted approve's 409
              body (checks_failing), regardless of what pr.checks itself says. */}
          <FailingChecksPanel
            checks={failingChecks}
            detailState={detailState}
            detailMessage={detailMessage}
          />

          {/* Files. An EMPTY changeset is called out rather than rendered as blank
              space: spartan #880 was one commit ahead of develop with zero files, and
              the page showed pills, an Approve button and nothing between them. A
              reviewer could not tell "this PR contains nothing" from "the diff failed
              to load", and approving it would deploy nothing while marking the
              operator's request shipped. */}
          {pr.files.length === 0 ? (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <p className="font-medium">This pull request changes no files.</p>
              <p className="mt-1">
                There is nothing to review and nothing to deploy — approving it would mark the
                request as shipped without changing anything. This usually means the editor's
                draft was identical to the published version when it was proposed. Close it and
                re-apply the edit.
              </p>
            </div>
          ) : (
          <div className="space-y-3">
            {pr.files.map((f) => (
              <div key={f.path} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                  <span className="font-mono text-xs text-gray-700 flex-1 truncate">{f.path}</span>
                  <span className="text-[10px] text-gray-400">{f.status}</span>
                  <span className="text-[10px] text-green-600">+{f.additions}</span>
                  <span className="text-[10px] text-red-600">−{f.deletions}</span>
                </div>
                <div className="px-3 py-2">
                  <PatchView patch={f.patch} />
                </div>
              </div>
            ))}
          </div>
          )}

          {actionError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {actionError}
              {approveOutcome?.promoteFailed && (
                <>
                  {' '}<Link to="/updates" className="underline">Go to Updates</Link>
                </>
              )}
            </div>
          )}

          {approveResult && (
            <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
              {approveResult.queued
                ? 'Approved — production deploy will run automatically once checks pass (~1 min).'
                : approveResult.promoted
                  ? 'Approved. It is on its way to production; the steps above show when it is live.'
                  : 'Merged to develop — deploy is in progress.'}
            </div>
          )}

          {denyDone && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
              Denied and closed.
              {denyBranchError && (
                <>
                  <p className="mt-2 text-amber-700">
                    Change denied, but its publish branch could not be removed. Publishing this version
                    again remains blocked; remove <code>{denyBranchError.branch}</code> and retry.
                  </p>
                  <p className="mt-1 text-xs text-gray-500">{denyBranchError.detail}</p>
                </>
              )}
              {denyStatusError && (
                <>
                  <p className="mt-2 text-amber-700">
                    The PR is closed, but the change could not be marked declined. It will keep
                    reading proposed on the Improvements page until the next status sweep.
                  </p>
                  <p className="mt-1 text-xs text-gray-500">{denyStatusError}</p>
                </>
              )}
            </div>
          )}

          {/* A core change is read-only here — say so plainly and offer nothing
              the server would refuse. Deliberately ABSENT rather than
              rendered-and-disabled: a greyed Approve invites a support question
              about permissions, when the truth is simply that this is not the
              fleet's change to take. */}
          {isCoreChange && pr.state === 'open' && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
              This change comes from the platform, not from this fleet, so it is
              reviewed and approved centrally. You can read all of it here, and it
              reaches this fleet when it ships.
            </div>
          )}

          {/* fleet_owner-only actions, for this fleet's OWN changes */}
          {canApprove && !isCoreChange && pr.state === 'open' && !denyDone && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => { void handleApprove() }}
                disabled={busy || pr.checks === 'failing' || pr.checks === 'pending' || pr.checks === 'stalled' || countedFailures(failingChecks).length > 0}
                title={approveDisabledReason(pr.checks, failingChecks)}
                className="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {busy ? 'Working…' : 'Approve & Deploy'}
              </button>
              <button
                type="button"
                onClick={() => { setDenying(true); setDenyReason(''); setActionError(null) }}
                disabled={busy}
                className="px-4 py-1.5 text-sm border border-red-300 text-red-600 rounded hover:bg-red-50 disabled:opacity-50 transition-colors"
              >
                Deny
              </button>
              {approveDisabledReason(pr.checks, failingChecks) && (
                <span className={`text-xs font-medium ${pr.checks === 'pending' && countedFailures(failingChecks).length === 0 ? 'text-amber-600' : 'text-red-600'}`}>
                  {approveDisabledReason(pr.checks, failingChecks)}
                </span>
              )}
            </div>
          )}

          {/* Terminal state — a closed PR has nothing left to approve. Explain why
              there is no action rather than silently omitting the buttons.
              ADR-110 Layer B: a merged PR's copy reflects the ACTUAL Deploy
              Fleet run, not just that the merge happened — merged ≠ deployed. */}
          {pr.state !== 'open' && (
            pr.merged ? (
              isDeployLive(pr.deploy) ? (
                <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                  ✓ This change is merged and live in production — no action needed.
                </div>
              ) : isDeployFailed(pr.deploy) ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  ✗ This change merged, but the production deploy FAILED
                  {pr.deploy?.url && (
                    <>
                      {' '}— <a href={pr.deploy.url} target="_blank" rel="noreferrer" className="underline">view the failed run</a>
                    </>
                  )}
                  . The change is safely on develop/main; retry the deploy from the fleet's deploy workflow.
                </div>
              ) : (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  Merged — deploying to production…
                </div>
              )
            ) : (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                This PR was closed without merging (denied). No action needed.
              </div>
            )
          )}

          {canApprove && !isCoreChange && pr.state === 'open' && (denyDone) && (
            <p className="text-xs text-gray-500">This PR was just denied and closed.</p>
          )}

          {!canApprove && !isCoreChange && pr.state === 'open' && (
            <p className="text-xs text-gray-500">View only — ask the fleet owner to approve or deny.</p>
          )}
        </div>
      )}

      {/* Deny reason modal */}
      {denying && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-1">Deny PR #{pr?.number}</h3>
            <p className="text-sm text-gray-500 mb-3">The reason is posted as a comment and the PR is closed.</p>
            <textarea
              autoFocus
              value={denyReason}
              onChange={(e) => setDenyReason(e.target.value)}
              rows={3}
              placeholder="Why are you denying this change?"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <div className="flex justify-end gap-3 mt-3">
              <button type="button" onClick={() => setDenying(false)} disabled={busy} className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              <button type="button" onClick={() => { void handleDeny() }} disabled={busy || !denyReason.trim()} className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50">
                {busy ? 'Denying…' : 'Deny & close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
